import {
  LayoutDashboard, Users, BookOpen, Wallet, Building2,
  Smartphone, Briefcase, UserCheck, Library, User,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * What /features shows a visitor: a curated highlight reel per role.
 *
 * IMPORTANT — this file is deliberately NOT a mirror of the product.
 *
 * It used to be. Every group below was a copy of a real sidebar category from
 * components/common/Sidebar.jsx, in sidebar order, with the sidebar's own
 * labels, its sub-menus and its per-category counts. Read together with
 * featureScreenshots.ts (which mapped a real capture to each individual menu
 * item) it amounted to a complete build spec for Kaympus, published on a public
 * marketing page and shipped in the client bundle where a competitor could read
 * it without even opening the site.
 *
 * The rule now: this sells outcomes, not menus.
 *
 *  1. Never copy a sidebar label. "Peoples › Students / Staff / Teachers" tells
 *     a rival how to build the nav. "One permanent record per student" tells a
 *     principal why they'd want it. Write the second one.
 *  2. Never mirror the real menu tree — not its grouping, not its order, and
 *     not its counts. The groups here are a marketing grouping that happens to
 *     cover the same ground.
 *  3. Highlights only. Seventeen groups, three capability lines each over in
 *     featureNarratives.ts. If the exhaustive list is what someone wants, that
 *     is what the demo is for — the CTA says exactly that.
 *  4. Only claim what ships. Every line is something a buyer can hold us to on
 *     a demo call.
 *
 * A group carries a name and a one-line tagline, and nothing else.
 *
 * There used to be an `items` list (and a `subGroups` tree under it) that the
 * rail rendered as an expandable menu. Two problems: the tree was the sidebar
 * shape this rewrite exists to remove, and once the labels became outcome
 * sentences rather than menu words, a nav-shaped control was the wrong place to
 * put them — it wrapped every line to two and filled the rail with a generic
 * fallback icon.
 *
 * The capability list now lives once, in `provides` in featureNarratives.ts,
 * rendered as the "What you get" card where full sentences read properly. The
 * rail is a rail.
 */
/** Which tab on /features a group belongs to. */
export type RoleKey = "admin" | "teacher" | "staff" | "student";

export type FeatureGroup = {
  id: string;
  title: string;
  icon: LucideIcon;
  color: string;
  /** Four to seven words. It sits under the title in the rail. */
  tagline: string;
  /**
   * The role tab this group appears under. Lives here rather than in the
   * catalogue component so the tab membership has one owner: the rail filters
   * on it, and a footer link needs it to know which tab to open.
   */
  role: RoleKey;
};

export const FEATURE_GROUPS: FeatureGroup[] = [
  // ── Admin ────────────────────────────────────────────────────────────────
  {
    id: "admin-overview",
    title: "School at a Glance",
    icon: LayoutDashboard,
    color: "#0284C7",
    tagline: "Live numbers, exceptions first",
    role: "admin",
  },
  {
    id: "admin-people",
    title: "Students & Staff Records",
    icon: Users,
    color: "#DC2626",
    tagline: "One record per person",
    role: "admin",
  },
  {
    id: "admin-academics",
    title: "Academics & Examinations",
    icon: BookOpen,
    color: "#D97706",
    tagline: "Timetable to report card",
    role: "admin",
  },
  {
    id: "admin-finance",
    title: "Fees, Payroll & Compliance",
    icon: Wallet,
    color: "#65A30D",
    tagline: "Money in, money out",
    role: "admin",
  },
  {
    id: "admin-operations",
    title: "Campus & Communication",
    icon: Building2,
    color: "#3D5EE1",
    tagline: "Facilities, notices, website",
    role: "admin",
  },

  // ── Teacher ──────────────────────────────────────────────────────────────
  {
    id: "teacher-day",
    title: "My Teaching Day",
    icon: LayoutDashboard,
    color: "#0284C7",
    tagline: "Today's periods and attendance",
    role: "teacher",
  },
  {
    id: "teacher-classroom",
    title: "Classwork & Assessment",
    icon: BookOpen,
    color: "#D97706",
    tagline: "Homework, tests and results",
    role: "teacher",
  },
  {
    id: "teacher-app",
    title: "Teacher Mobile App",
    icon: Smartphone,
    color: "#B45309",
    tagline: "The classroom, in your pocket",
    role: "teacher",
  },
  {
    id: "teacher-self",
    title: "Leave, Salary & Reports",
    icon: Briefcase,
    color: "#4338CA",
    tagline: "Your own leave and payslips",
    role: "teacher",
  },

  // ── Staff ────────────────────────────────────────────────────────────────
  {
    id: "staff-workspace",
    title: "Your Own Login",
    icon: UserCheck,
    color: "#4338CA",
    tagline: "Scoped to your job",
    role: "staff",
  },
  {
    id: "staff-app",
    title: "Staff Mobile App",
    icon: Smartphone,
    color: "#0F766E",
    tagline: "Punch in, apply, download",
    role: "staff",
  },
  {
    id: "staff-frontdesk",
    title: "Front Office & Fee Counter",
    icon: Wallet,
    color: "#65A30D",
    tagline: "The counter and the queue",
    role: "staff",
  },
  {
    id: "staff-facilities",
    title: "Library, Hostel & Facilities",
    icon: Library,
    color: "#4D7C0F",
    tagline: "Books, beds and buses",
    role: "staff",
  },

  // ── Student & Parent ─────────────────────────────────────────────────────
  {
    id: "student-home",
    title: "The Family's View",
    icon: User,
    color: "#DC2626",
    tagline: "One child, one clear view",
    role: "student",
  },
  {
    id: "student-app",
    title: "Parent & Student App",
    icon: Smartphone,
    color: "#BE123C",
    tagline: "Alerts the day they happen",
    role: "student",
  },
  {
    id: "student-academics",
    title: "Classwork, Exams & Results",
    icon: BookOpen,
    color: "#D97706",
    tagline: "Homework, exams and results",
    role: "student",
  },
  {
    id: "student-fees",
    title: "Fees & Payments",
    icon: Wallet,
    color: "#65A30D",
    tagline: "Due, paid, and payable online",
    role: "student",
  },
];

/**
 * Headline figures for the section intro and the closing CTA.
 *
 * Declared, not derived. They describe the product; FEATURE_GROUPS above
 * describes the page, and the page is a highlight reel — deriving the headline
 * from it would either undersell the system or push us back towards publishing
 * the exhaustive list to make the number come out right. That list is what the
 * demo is for.
 *
 * These are the figures the page has always quoted (84 across 15 categories);
 * they used to be computed from a thematic catalogue that also lived in this
 * file and was never rendered. Update them by hand when the product gains or
 * loses a module — nothing derives them now, so nothing will flag them as
 * stale.
 */
export const TOTAL_FEATURES = 84;
export const TOTAL_CATEGORIES = 17;

/**
 * The six groups the footer links to, in page order.
 *
 * A footer column can hold six lines before it starts competing with the
 * columns beside it, so this is a cut of a cut: six of the seventeen, chosen
 * for breadth rather than for rank — between them they cover records, money,
 * academics, the non-teaching side of the campus, facilities and the phone
 * apps, and they span three of the four role tabs.
 *
 * Ids, not labels. The footer used to carry its own six hand-written titles
 * pointing at anchors (#students, #fees, #academics …) that no element on
 * /features has ever had, so every link landed the visitor at the top of the
 * page on a category nobody chose. Naming groups means the label, the icon and
 * the destination all come from the row the rail actually renders, and a
 * retitled group updates the footer with it.
 */
const FOOTER_FEATURE_IDS = [
  "admin-people",
  "admin-academics",
  "admin-finance",
  "admin-operations",
  "staff-facilities",
  "student-app",
];

/**
 * Footer link rows, resolved against FEATURE_GROUPS.
 *
 * An id that no longer exists drops out rather than throwing: this is module
 * scope, and a renamed group should cost the footer one line, not the render of
 * every page that has a footer.
 */
export const FOOTER_FEATURE_LINKS = FOOTER_FEATURE_IDS.map((id) =>
  FEATURE_GROUPS.find((g) => g.id === id)
)
  .filter((g): g is FeatureGroup => Boolean(g))
  .map((g) => ({
    id: g.id,
    label: g.title,
    tagline: g.tagline,
    icon: g.icon,
    /* The catalogue reads this hash on mount and on hashchange, opens the
       group's role tab and selects it — see FeatureCatalogue.tsx. */
    href: `/features#${g.id}`,
  }));
